import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { baseUrl } from '../../utils/axios'
import { toast } from 'react-toastify'
import { handleErrors, formatParams, getParams } from 'utils/helpers'
import { AxiosResponse } from 'axios'
import { IPaginatedResponse } from 'types/api/general.interface'
import { IAccommodationSlice } from 'types/accommodation.interface'
import {
  IAccommodationInfoResponse,
  IAccommodationSingleResponse,
  ICheckInPostRequest,
  IGetAllAccommodationsSingleResponse,
  IGetAllAccommodationUsersSingleResponse,
} from 'types/api/accommodationApi.interface'
import { accommodationCategories, accommodationTypes } from 'pages/Accomodation/accommodationData'
import { ICheckInState } from 'pages/Accomodation/CheckIn'

const initialState: IAccommodationSlice = {
  isLoading: false,
  accommodations: [],
  singleAccommodationInfo: null,
  totalCount: 0,
  totalPages: 0,
  accomodationUsers: {
    data: [],
    totalCount: 0,
    totalPages: 0,
  },
}

export const getAccommodations = createAsyncThunk(
  'accommodations/getAccommodations',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationsSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getAccommodationUsers = createAsyncThunk(
  'accommodations/getAccommodationUsers',
  async (params: any, thunkAPI) => {
    try {
      params = formatParams(params)
      const resp: AxiosResponse<IPaginatedResponse<IGetAllAccommodationUsersSingleResponse>> =
        await baseUrl.get('accommodation/management/accommodation/users', { params })
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodationInfo = createAsyncThunk(
  'accommodations/getSingleAccommodationInfo',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationInfoResponse> = await baseUrl.get(
        'accommodation/management/accommodation/info/' + id,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const checkIn = createAsyncThunk(
  'accommodations/checkIn',
  async (state: ICheckInState, thunkAPI) => {
    try {
      const obj: ICheckInPostRequest = {
        accommodationId: state.accommodation.value,
        userIds: state.users.map((u) => u.value),
        guests: state.guests,
        startDate: state.startDate.toISOString(),
        numberOfNights: state.numberOfNights,
      }
      const resp = await baseUrl.post('accommodation/management/accommodation/check-in', obj)
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)
export const checkOut = createAsyncThunk(
  'accommodations/checkOutUser',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.post('accommodation/management/accommodation/check-out/', {
        accommodationAssignmentRequestId: id,
      })
      await thunkAPI.dispatch(getAccommodations(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const getSingleAccommodation = createAsyncThunk(
  'accommodations/getSingleAccomodation',
  async (id: string, thunkAPI) => {
    try {
      const resp: AxiosResponse<IAccommodationSingleResponse> = await baseUrl.get(
        'accommodation/management/accommodation/' + id,
      )
      const state = {
        id: resp.data.id,
        name: resp.data.name,
        address: resp.data.address,
        category: accommodationCategories.find((c) => c.value === resp.data.category),
        type: accommodationTypes.find((t) => t.value === resp.data.type),
        startDate: new Date(resp.data.startDate),
        endDate: new Date(resp.data.endDate),
        rooms: resp.data.rooms,
      }
      return state
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const createAccommodation = createAsyncThunk(
  'accommodations/createAccomodation',
  async (state, thunkAPI) => {
    const req = {}
    try {
      const resp: AxiosResponse<IGetAllAccommodationsSingleResponse> = await baseUrl.post(
        'accommodation/management/accommodation',
        req,
      )
      thunkAPI.dispatch(getAccommodations(getParams({ location })))
      return resp.data
    } catch (error) {
      console.log(error)
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const editAccommodation = createAsyncThunk(
  'accommodations/editAccomodation',
  async (state, thunkAPI) => {
    const req = {}
    try {
      const resp: AxiosResponse<IGetAllAccommodationsSingleResponse> = await baseUrl.patch(
        'accommodation/management/accommodation/' + state,
        req,
      )
      return resp.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

export const deleteAccommodation = createAsyncThunk(
  'accommodations/deleteAccomodation',
  async (id: number, thunkAPI) => {
    try {
      await baseUrl.delete('accommodation/management/accommodation/' + id)
      await thunkAPI.dispatch(getAccommodations(getParams({ location })))
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  },
)

const accommodationSlice = createSlice({
  name: 'accommodation',
  initialState,
  reducers: {
    clearAccommodations: (state) => {
      state.accommodations = []
    },
    clearAccommodationInfo: (state) => {
      state.singleAccommodationInfo = null
    },
    clearAccommodationUsers: (state) => {
      state.accomodationUsers = {
        data: [],
        totalCount: 0,
        totalPages: 0,
      }
    },
  },

  extraReducers: {
    [getAccommodations.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodations.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationsSingleResponse> },
    ) => {
      state.accommodations = payload.data
      state.totalCount = payload.totalCount
      state.totalPages = payload.totalPages
      state.isLoading = false
    },
    [getAccommodations.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getAccommodationUsers.pending.type]: (state) => {
      state.isLoading = true
    },
    [getAccommodationUsers.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IPaginatedResponse<IGetAllAccommodationUsersSingleResponse> },
    ) => {
      state.accomodationUsers = payload
      state.isLoading = false
    },
    [getAccommodationUsers.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkIn.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkIn.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-in successful')
      state.isLoading = false
    },
    [checkIn.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [checkOut.pending.type]: (state) => {
      state.isLoading = true
    },
    [checkOut.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Check-out successful')
      state.isLoading = false
    },
    [checkOut.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodationInfo.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodationInfo.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload: IAccommodationInfoResponse },
    ) => {
      state.singleAccommodationInfo = payload
      state.isLoading = false
    },
    [getSingleAccommodationInfo.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [createAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [createAccommodation.fulfilled.type]: () => {
      toast.success('Accommodation created successfully')
    },
    [createAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [editAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [editAccommodation.fulfilled.type]: () => {
      toast.success('Accommodation updated successfully')
    },
    [editAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [deleteAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [deleteAccommodation.fulfilled.type]: (state: IAccommodationSlice) => {
      toast.success('Accommodation deleted successfully')
      state.isLoading = false
    },
    [deleteAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
    [getSingleAccommodation.pending.type]: (state) => {
      state.isLoading = true
    },
    [getSingleAccommodation.fulfilled.type]: (
      state: IAccommodationSlice,
      { payload }: { payload },
    ) => {
      state.isLoading = false
    },
    [getSingleAccommodation.rejected.type]: (state, { payload }) => {
      state.isLoading = false
      handleErrors(payload)
    },
  },
})

export const { clearAccommodations, clearAccommodationInfo, clearAccommodationUsers } =
  accommodationSlice.actions
export default accommodationSlice.reducer
